/**
 * @file
 * @author dailin01
 */
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import classNames from 'classnames';
import omit from 'rc-util/lib/omit';
import { tuple } from '../_util/type';
import LoadingIcon from './LoadingIcon';
import ButtonGroup from './button-group';
import { ConfigContext } from '../config-provider';
import SizeContext from '../config-provider/SizeContext';
const ButtonTypes = tuple('default', 'primary', 'text', 'highlight', 'enhance', 'actiontext');
const ButtonShapes = tuple('circle', 'round');
const ButtonHTMLTypes = tuple('submit', 'button', 'reset');
const InternalButton = (props, ref) => {
    const { type = 'default', prefixCls: customizePrefixCls, loading = false, icon = null, size: customizeSize, children, 
    // React does not recognize the `htmlType` prop on a DOM element. Here we pick it out of `rest`.
    htmlType = 'button', className } = props, rest = __rest(props, ["type", "prefixCls", "loading", "icon", "size", "children", "htmlType", "className"]);
    const characterLen = (s) => {
        let result = 0;
        for (let i = 0; i < s.length; i++) {
            if (s.charCodeAt(i) > 255) {
                result += 2;
            }
            else {
                result++;
            }
        }
        return result;
    };
    const textLimited = children && typeof children === 'string' && characterLen(children) < 8;
    const hasIcon = loading || !!icon;
    const onlyIcon = !!icon && (children === null || children === undefined);
    const [innerLoading, setLoading] = React.useState(!!loading);
    const buttonRef = ref || React.createRef();
    const size = React.useContext(SizeContext);
    const sizeClassNameMap = { large: 'lg', small: 'sm', middle: undefined };
    const sizeFullname = customizeSize || size;
    const sizeCls = (sizeFullname && sizeClassNameMap[sizeFullname]) || '';
    const { getPrefixCls } = React.useContext(ConfigContext);
    const prefixCls = getPrefixCls('btn', customizePrefixCls);
    const classes = classNames(prefixCls, {
        [`${prefixCls}-${type}`]: type,
        [`${prefixCls}-${sizeCls}`]: sizeCls,
        [`${prefixCls}-loading`]: loading,
        [`${prefixCls}${sizeCls ? '-' + sizeCls : ''}-text-limited`]: textLimited,
        [`${prefixCls}${sizeCls ? '-' + sizeCls : ''}-has-icon`]: hasIcon,
        [`${prefixCls}${sizeCls ? '-' + sizeCls : ''}-only-icon`]: onlyIcon,
        [`${className}`]: className,
    });
    // loading
    let loadingOrDelay;
    if (typeof loading === 'object' && loading.delay) {
        loadingOrDelay = loading.delay || true;
    }
    else {
        loadingOrDelay = !!loading;
    }
    React.useEffect(() => {
        let delayTimer = null;
        if (typeof loadingOrDelay === 'number') {
            delayTimer = window.setTimeout(() => {
                delayTimer = null;
                setLoading(loadingOrDelay);
            }, loadingOrDelay);
        }
        else {
            setLoading(loadingOrDelay);
        }
        return () => {
            if (delayTimer) {
                window.clearTimeout(delayTimer);
                delayTimer = null;
            }
        };
    }, [loadingOrDelay]);
    const linkButtonRestProps = omit(rest, ['navigate']);
    // Icon
    const iconNode = innerLoading ? <LoadingIcon /> : icon;
    // 不需要给汉字中间加空格
    const kids = typeof children === 'number' || typeof children === 'string' ? (<span>{children}</span>) : (children);
    if (linkButtonRestProps.href !== undefined) {
        return (<a {...linkButtonRestProps} className={classes} ref={buttonRef}>
                {iconNode}
                {kids}
            </a>);
    }
    return (<button {...rest} ref={buttonRef} type={htmlType} className={classes}>
            {iconNode}
            {kids}
        </button>);
};
const Button = React.forwardRef(InternalButton);
Button.displayName = 'Button';
Button.Group = ButtonGroup;
Button.__ACUD_BUTTON = true;
export default Button;
